import type { ConsiderationRow } from '@/api/considerations';
import type { RoleOption } from '@/api/vacancies';

/**
 * One choice in §6.3's vacancy filter: the vacancy's id, and the **label** a person reads.
 *
 * ⚠️ **The field was `title` until 2026-11-03 and the rename is the point of the change**
 * (TASK-187). A title is not an identifier: the first real ADP import gave this filter 96 options
 * of which *Manual QA Engineer* was eight, and the label a reader picks from now carries the
 * requisition number and the hiring manager beside the title (`vacancyIdentity.ts`). Calling the
 * field `title` while it held three facts would be the next reader's bug.
 */
export interface VacancyOption {
  id: number;
  label: string;
  /**
   * ⚠️ **What the collapsed trigger says, when the label does not survive it** (D-1, 2026-11-04) —
   * `Select`'s own optional field, passed straight through. Absent on both fallback paths below,
   * which carry bare titles: there is nothing in them to shorten and nothing there to lose.
   */
  triggerLabel?: string;
}

/**
 * **The vacancy filter's options.**
 *
 * ⚠️ **This module existed because §6.3's filter had no single source** — `GET /api/v1/roles/` answers an
 * **Interviewer 403**, so their options were built from the `role_id`/`role_title` pairs already on their
 * own rows and the request was never sent. Backend TASK-108 gave the filter a route every group may call
 * (`/api/v1/roles/options/`, narrowed per caller), so the *source* is no longer this module's business.
 *
 * What is left is the part only the client can do, and it is why the module was not deleted: keeping a
 * **selected** vacancy in the list when the source cannot name it, and falling back to the rows on screen
 * when the request **failed** — a narrower list with a sentence beside it, rather than an empty control.
 *
 * ⚠️ **The options are derived from the current page and nothing is remembered across renders.** An
 * accumulator kept in a ref would grow a nicer list and would also survive a change of viewer under
 * a mounted screen — the exact shape of the mistake Sprint 8's `placeholderData` made with figures,
 * applied to vacancy titles. A narrower list is the honest cost.
 *
 * The consequence, stated rather than hidden: once an Interviewer picks a vacancy, the rows on
 * screen are that vacancy's, so the list is *that* vacancy plus "All vacancies" — switching means
 * clearing first. A route that lets an Interviewer read the titles of the vacancies they are
 * assigned to would remove this, and it is a backend item, not something the client can work around.
 */
export function vacancyOptions(input: {
  /**
   * The options route's answer, when it has arrived. `undefined` means *no list*: not answered yet, or
   * **failed** — and in both cases the rows below are the source. (Before backend TASK-108 it also meant
   * *"never requested, because this viewer is an Interviewer"*, which no longer happens.)
   *
   * ⚠️ The failure case is a **narrower** list, not a hidden control and not a silent one:
   * `VacancyFilter` renders the sentence and a Retry beside it, so the viewer is told that what they
   * are looking at is the vacancies on this page rather than the tenant's catalogue.
   */
  catalogue?: RoleOption[];
  /** The rows currently on screen, in the order the API sent them. */
  rows: ConsiderationRow[];
  /** What the address bar says is filtered to, so it can never be missing from the list. */
  selected: number | null;
  /**
   * How one option of the route's answer reads — the caller translates it, through the module
   * §6.5's block shares (`vacancyIdentity.ts`). It is a callback rather than a `t` passed in
   * because this module's job is **which** vacancies are offered and never **how** they are worded.
   *
   * ⚠️ **It returns the option's wording whole — both strings — rather than the label alone**
   * (D-1, 2026-11-04). The trigger needs a shorter form than the list does, and a *second*
   * callback for the second string would be two things to keep in step for one wording, which is
   * the shape BUG-088 punishes. One callback, one caller, one place both are decided.
   */
  labelForOption: (option: RoleOption) => Omit<VacancyOption, 'id'>;
  /** The label for a selected vacancy neither source can name — the caller translates it. */
  labelForUnknown: (id: number) => string;
}): VacancyOption[] {
  const { catalogue, rows, selected, labelForOption, labelForUnknown } = input;

  const options =
    catalogue !== undefined
      ? // The route's own order is meaningful — open vacancies first, then by title, restated from
        // `Role.Meta.ordering` server-side. It is passed through rather than re-sorted here.
        catalogue.map((role) => ({ id: role.id, ...labelForOption(role) }))
      : fromRows(rows);

  /**
   * ⚠️ **A selected vacancy that is in neither source still has to appear**, or the control shows
   * "All vacancies" while `role_id=` is on the wire — §6.3's own named failure (*"a filtered list
   * that looks unfiltered is how people conclude a candidate was lost"*) arriving through a
   * dropdown. It happens for an Interviewer whose filtered page came back empty, and for anyone
   * whose vacancy is past the catalogue's first page.
   */
  if (selected !== null && !options.some((option) => option.id === selected)) {
    return [...options, { id: selected, label: labelForUnknown(selected) }];
  }

  return options;
}

/**
 * The pairs on the rows, deduplicated. Sorted by title: the rows arrive `updated_at` descending,
 * which is the right order for a table and no order at all for a list of names to pick from.
 *
 * ⚠️ **These labels are the bare title, and that is the stated cost of the failure path.**
 * `ConsiderationTableRowOut` carries `role_id` and `role_title` and neither of the two facts that
 * tell two identically-titled vacancies apart, so when the options route has failed the control
 * offers a **narrower** list in a **narrower** wording — with `VacancyFilter`'s sentence and Retry
 * beside it saying so. Widening it would mean a request per row to a route that was just refused.
 */
function fromRows(rows: ConsiderationRow[]): VacancyOption[] {
  const byId = new Map<number, string>();
  for (const row of rows) byId.set(row.role_id, row.role_title);

  return [...byId.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((one, other) => one.label.localeCompare(other.label));
}
